import React from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Question } from '../../../api/questions';
import { Check, X } from 'lucide-react';

interface QuestionViewDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  question: Question | null;
}

export function QuestionViewDialog({ isDark, isOpen, onOpenChange, question }: QuestionViewDialogProps) {
  if (!question) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-3xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-[#0F172A]')}>
            Question
            <Badge variant="outline" className="text-xs">
              {question.type}
            </Badge>
            {question.difficulty && (
              <Badge variant="outline" className="text-xs">
                {question.difficulty}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-3">
          <div className={cn('text-sm whitespace-pre-wrap', isDark ? 'text-slate-200' : 'text-slate-800')}>
            {question.text}
          </div>

          {question.image && (
            <img src={question.image} alt="" className="max-h-60 rounded border" />
          )}

          {question.options?.length > 0 && (
            <div className="space-y-1.5">
              {question.options.map((o) => (
                <div
                  key={o.id || o.label}
                  className={cn(
                    'flex items-start gap-2 p-2 rounded text-sm',
                    o.is_correct
                      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                      : isDark
                        ? 'bg-slate-800 text-slate-300'
                        : 'bg-slate-50 text-slate-700',
                  )}
                >
                  <span className="font-bold w-6">{o.label}.</span>
                  <span className="flex-1">{o.text}</span>
                  {o.is_correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4 opacity-30" />}
                </div>
              ))}
            </div>
          )}

          {question.sub_questions?.length > 0 && (
            <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              {question.sub_questions.map((sq, idx) => (
                <div key={sq.id || idx} className="space-y-1.5">
                  <div className={cn('text-sm font-medium', isDark ? 'text-slate-200' : 'text-slate-800')}>
                    {idx + 1}. {sq.text}
                  </div>
                  {sq.sub_options?.map((so) => (
                    <div
                      key={so.id || so.label}
                      className={cn(
                        'flex items-start gap-2 p-1.5 ml-4 rounded text-xs',
                        so.is_correct
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                          : isDark
                            ? 'text-slate-400'
                            : 'text-slate-600',
                      )}
                    >
                      <span className="font-bold w-5">{so.label}.</span>
                      <span className="flex-1">{so.text}</span>
                      {so.is_correct && <Check className="w-3 h-3" />}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {question.explanation && (
            <div
              className={cn(
                'p-3 rounded text-sm',
                isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-700',
              )}
            >
              <div className="text-xs uppercase font-semibold mb-1 opacity-60">Explanation</div>
              {question.explanation}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
